/**
 * OfferManagement — slim composition for the Offers page.
 *
 * Phase 7 refactor: this file used to be a 1,645-line monolith. The list,
 * card, wizard, modals, notifications, history, and toaster have all moved
 * to their own components in this folder; the giant local hooks moved to
 * ./hooks. This container is now a thin orchestrator.
 *
 * Backwards-compatible props:
 *   - buyerId / userRole / role / initialPropertyId / onViewHouse  (legacy)
 *   - currentUser / currentRole / selectedOfferId / onClearSelected (new)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import APIAgent from '../../agents/apiAgent';
import PageHeader from '../ui/PageHeader';
import SubTabs from '../ui/SubTabs';

import { useOffersData } from './hooks/useOffersData';
import { useToast } from './hooks/useToast';

import { OffersList, applyToolbarState, DEFAULT_TOOLBAR_STATE } from './list';
import { isClosedByListingDelete, isOfferClosed } from './utils';
import { OfferWizard, PropertyBrowse } from './wizard';
import {
  CounterOfferModal,
  AcceptOfferModal,
  RejectOfferModal,
  WithdrawOfferModal,
} from './modals';
import OfferHistoryDrawer from './history/OfferHistoryDrawer';
import DepositVerificationModal from './DepositVerificationModal';
import Toaster from './Toaster';

import './OfferManagement.css';

export default function OfferManagement({
  // legacy props
  buyerId,
  userRole,
  role,
  initialPropertyId,
  onViewHouse,
  // new props
  currentUser,
  currentRole,
  selectedOfferId,
  onClearSelected,
  onNavigateToNegotiations,
}) {
  const userId = currentUser?.id ?? buyerId ?? 1;
  const resolvedRole = currentRole || userRole || role || 'buyer';
  const isSeller = resolvedRole === 'seller';

  // ── Data hooks ────────────────────────────────────────────────────────
  const offersData = useOffersData({ userId, role: resolvedRole });
  const toast = useToast();

  // ── Local UI state ────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(isSeller ? 'list' : 'create');
  const [toolbarState, setToolbarState] = useState(DEFAULT_TOOLBAR_STATE);

  const [actionModal, setActionModal] = useState(null); // { kind, offer }
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // The API error's structured `detail` (e.g. a 422 offer_contingencies_invalid).
  const [submitErrorDetail, setSubmitErrorDetail] = useState(null);

  const [historyDrawer, setHistoryDrawer] = useState({ open: false, offer: null });
  const [wizardProperty, setWizardProperty] = useState(null);
  const [depositOffer, setDepositOffer] = useState(null);

  // Reset role-derived defaults when role changes.
  useEffect(() => {
    setActiveTab(isSeller ? 'list' : 'create');
  }, [isSeller]);

  // ── Deep-link: open history drawer for a specific offer id. ──────────
  useEffect(() => {
    if (!selectedOfferId || !offersData.offers.length) return;
    const offer = offersData.offers.find((o) => o.id === selectedOfferId);
    if (offer) {
      setHistoryDrawer({ open: true, offer });
      if (typeof onClearSelected === 'function') onClearSelected();
    }
  }, [selectedOfferId, offersData.offers, onClearSelected]);

  // ── Toolbar persistence + visible offers ─────────────────────────────
  const handleToolbarChange = useCallback((patch) => {
    setToolbarState((prev) => ({ ...prev, ...patch }));
  }, []);

  const visibleOffers = useMemo(
    () => applyToolbarState(offersData.offers, toolbarState),
    [offersData.offers, toolbarState]
  );

  const hasFilters =
    !!toolbarState.search ||
    (toolbarState.statusFilter && toolbarState.statusFilter !== 'all');
  const clearFilters = useCallback(() => setToolbarState(DEFAULT_TOOLBAR_STATE), []);

  // ── Card actions ──────────────────────────────────────────────────────
  const onCardAction = useCallback((key, offer) => {
    // A closed offer is read-only (legal copy §15.3), and a deleted listing has no page.
    if (isOfferClosed(offer) && ['accept', 'counter', 'reject', 'withdraw', 'verifyDeposit'].includes(key)) return;
    if (key === 'viewHouse' && isClosedByListingDelete(offer)) return;
    // For active negotiation actions and viewing accepted offers, go to the workspace
    if (['accept', 'counter', 'reject', 'withdraw', 'view'].includes(key) && typeof onNavigateToNegotiations === 'function') {
      onNavigateToNegotiations({
        offerId: offer?.id,
        propertyId: offer?.property_id ?? offer?.house_id,
        propertyAddress: offer?.property_address,
      });
      return;
    }
    if (['accept', 'counter', 'reject', 'withdraw'].includes(key)) {
      setSubmitError(null);
      setSubmitErrorDetail(null);
      setActionModal({ kind: key, offer });
      // The counter form needs the listing (year built, paperwork category,
      // utilities) to decide which contingencies, deadlines and the
      // lead-paint election apply. Until it loads, the offer alone decides.
      const propertyId = offer?.property_id ?? offer?.house_id;
      if (key === 'counter' && propertyId) {
        Promise.resolve()
          .then(() => APIAgent.request(`/api/houses/${propertyId}`))
          .then((property) => {
            if (!property) return;
            setActionModal((prev) => (prev && prev.offer?.id === offer.id ? { ...prev, property } : prev));
          })
          .catch((err) => console.error('OfferManagement: failed to load property for counter', err));
      }
    } else if (key === 'history' || key === 'view') {
      setHistoryDrawer({ open: true, offer });
    } else if (key === 'viewHouse') {
      const id = offer?.house_id ?? offer?.property_id;
      if (typeof onViewHouse === 'function' && id) onViewHouse(id);
    } else if (key === 'verifyDeposit') {
      setDepositOffer(offer);
    }
  }, [onViewHouse, onNavigateToNegotiations]);

  // ── API handlers (preserve legacy contracts) ─────────────────────────
  const closeActionModal = () => setActionModal(null);

  const runAction = async (operation, successMessage) => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorDetail(null);
    try {
      await operation();
      setActionModal(null);
      toast.showSuccess(successMessage);
      offersData.refetch();
    } catch (err) {
      setSubmitError(err?.message || 'Request failed');
      setSubmitErrorDetail(err?.detail ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = (offer) => runAction(
    () => APIAgent.request(`/api/offers/${offer.id}/accept`, 'PUT'),
    'Offer accepted! Purchase agreement will be generated.'
  );
  const handleCounter = (p) => runAction(
    () => APIAgent.request(`/api/offers/${p.offer_id}/counter`, 'POST', {
      offer_price: p.offer_price,
      down_payment_percent: p.down_payment_percent,
      // QA D1/N15: omitted when blank, so the server keeps the deposit on the table.
      ...(p.earnest_money !== undefined ? { earnest_money: p.earnest_money } : {}),
      closing_date: p.closing_date,
      expires_on: p.expires_on,
      // Omitted when the counter left them unchanged: the server keeps the stored terms.
      ...(p.contingencies !== undefined ? { contingencies: p.contingencies } : {}),
      changes_description: p.changes_description || 'Counter-offer submitted',
    }),
    'Counter-offer submitted successfully!'
  );
  const handleReject = ({ offer, reason }) => runAction(
    // Send only what the seller wrote: the buyer sees this reason (QA F15).
    () => APIAgent.request(`/api/offers/${offer.id}/reject`, 'PUT', { reason: (reason || '').trim() || null }),
    'Offer rejected.'
  );
  const handleWithdraw = ({ offer, reason }) => runAction(
    () => APIAgent.request(`/api/offers/${offer.id}/withdraw`, 'PUT', { reason: reason || 'Changed mind' }),
    'Offer withdrawn.'
  );

  // ── Wizard flow ───────────────────────────────────────────────────────
  const handlePropertySelected = useCallback(async (house) => {
    if (!house?.seller_id) {
      toast.showError('This property is missing seller information and cannot be used to make an offer.');
      return;
    }
    // Browse/list responses omit mls_profile (heavy blob, projected out by the
    // repository), but the wizard needs water_source/sewer/hoa to decide which
    // property-specific contingencies to offer. Hydrate from the detail
    // endpoint; a failure just means those options stay hidden.
    let property = house;
    if (!house.mls_profile) {
      try {
        const detail = await APIAgent.request(`/api/houses/${house.id}`);
        if (detail) property = { ...house, ...detail };
      } catch (err) {
        console.error('OfferManagement: failed to load property details', err);
      }
    }
    setWizardProperty(property);
  }, [toast]);

  const handleWizardSubmit = async (form) => {
    if (!form.seller_id) { setSubmitError('Please select a property before submitting an offer.'); return; }
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorDetail(null);
    try {
      const payload = {
        ...form,
        earnest_money: form.earnest_money !== '' && form.earnest_money != null ? parseFloat(form.earnest_money) : null,
        contingencies: { ...form.contingencies, other: form.contingencies?.other || null },
      };
      await APIAgent.request('/api/offers', 'POST', payload);
      const addressLabel = wizardProperty?.address ? ` for ${wizardProperty.address}` : '';
      setWizardProperty(null);
      setActiveTab('list');
      toast.showSuccess(`Offer created successfully${addressLabel}!`);
      offersData.refetch();
    } catch (err) {
      setSubmitError(err?.message || "We couldn't submit your offer. Please try again.");
      setSubmitErrorDetail(err?.detail ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Tabs ──────────────────────────────────────────────────────────────
  const tabs = isSeller
    ? [{ key: 'list', label: 'Incoming offers' }]
    : [
        { key: 'list', label: 'My offers' },
        { key: 'create', label: 'New offer' },
      ];

  return (
    <div className="offer-management" data-testid="offer-management">
      <PageHeader
        eyebrow={isSeller ? 'Seller' : 'Buyer'}
        title={isSeller ? 'Offers on my listings' : 'My offers'}
        subtitle={
          isSeller
            ? 'Review and respond to offers from prospective buyers.'
            : 'Track your offers, counter, and submit new ones.'
        }
      />

      {tabs.length > 1 && (
        <SubTabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Offers navigation"
        />
      )}

      {activeTab === 'list' && (
        <OffersList
          offers={visibleOffers}
          loading={offersData.loading}
          error={offersData.error}
          currentRole={resolvedRole}
          toolbarState={toolbarState}
          onToolbarChange={handleToolbarChange}
          onRefresh={offersData.refetch}
          onAction={onCardAction}
          onViewHouse={(offer) => onCardAction('viewHouse', offer)}
          onViewHistory={(offer) => setHistoryDrawer({ open: true, offer })}
          onCreate={!isSeller ? () => setActiveTab('create') : undefined}
          onClearFilters={hasFilters ? clearFilters : undefined}
          rawOffersForCounts={offersData.offers}
        />
      )}

      {activeTab === 'create' && !isSeller && (
        <PropertyBrowse
          userId={userId}
          initialPropertyId={initialPropertyId}
          onSelect={handlePropertySelected}
        />
      )}

      <OfferWizard
        open={!!wizardProperty}
        onClose={() => setWizardProperty(null)}
        property={wizardProperty}
        buyerId={userId}
        onSubmit={handleWizardSubmit}
        submitting={submitting}
        submitError={submitError}
        submitErrorDetail={submitErrorDetail}
      />

      {(() => {
        const m = { offer: actionModal?.offer, currentRole: resolvedRole, submitting, error: submitError, onClose: closeActionModal };
        return (<>
          <CounterOfferModal  open={actionModal?.kind === 'counter'}  {...m} property={actionModal?.property} errorDetail={submitErrorDetail} onSubmit={handleCounter} />
          <AcceptOfferModal   open={actionModal?.kind === 'accept'}   {...m} onSubmit={handleAccept} />
          <RejectOfferModal   open={actionModal?.kind === 'reject'}   {...m} onSubmit={handleReject} />
          <WithdrawOfferModal open={actionModal?.kind === 'withdraw'} {...m} onSubmit={handleWithdraw} />
        </>);
      })()}

      <OfferHistoryDrawer
        open={historyDrawer.open}
        offer={historyDrawer.offer}
        offerId={historyDrawer.offer?.id}
        onClose={() => setHistoryDrawer({ open: false, offer: null })}
      />

      {depositOffer && (
        <DepositVerificationModal
          open
          offerId={depositOffer.id}
          earnestAmount={depositOffer.earnest_money}
          onComplete={(record) => {
            setDepositOffer(null);
            toast.showSuccess('Escrow receipt uploaded. The seller can now see it.');
            offersData.refetch();
          }}
          onDismiss={() => setDepositOffer(null)}
        />
      )}

      <Toaster toast={toast.toast} onDismiss={toast.dismiss} />
    </div>
  );
}
